/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { connect, useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { isEmpty, map, some, stubTrue } from 'lodash'
import { toast } from 'react-toastify'

import ScanQRGif from '../assets/gifs/scan-qr.gif'

import NetworkIndicator from '../containers/NetworkIndicator'
import BackButtonChevron from '../components/BackButtonChevron'
import GratuityHeader from '../components/GratuityHeader'
import SegmentedControlV2 from '../components/segmentedControl/SegmentedControl'
import ConnectingIndicator from '../components/ConnectingIndicator'
import useCreateOrderInProgress from '../hooks/useCreateOrderInProgress'
import useGetQRBalance from '../hooks/useGetQRBalance'
import { getOrderInProgress } from '../selectors/order'
import { centsToDollars } from '../utils/formatters'
import PeripheralBridge from '../utils/peripheralBridge'
import { KEY_CODES } from '../kiosk/utils/constants'
import { addPaymentToOrderInProgress, clearOrderInProgress, closeNoOpTender } from '../actions/order'
import styles from './ScanScreen.module.scss'
import { getDeviceMode } from '../VNMode/Selectors'
import { MODES } from '../VNMode/Reducer'
import { VNDialog } from 'vn-react-components/VNComponents/VNDialog'
import { clearCart } from '../actions/cart'
import { clearPromotionsOnOrder } from '../actions/promotion'
import { getPaymentProgress, getQrPaySuccess } from '../VNCustomerFacingDisplay/Selectors'
import { sendCFDScreenNav } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { CFD_SCREEN } from '../VNCustomerFacingDisplay/Reducer'
import { centsToFloat } from '../VNUtil/Money'
import { getTipsEnabled } from '../selectors/menus'
import { resetCFD, setQrPaySuccess } from '../VNCustomerFacingDisplay/ActionCreators'
import useRemoteOrderTotal from '../hooks/useRemoteOrderTotal'

const QRChoices = [
  { label: 'Scan QR Code', value: 'scan' },
  { label: 'Type QR Code', value: 'entry' },
]

const toastError = (message, classSize, isMobile) => {
  const options = {
    autoClose: true,
  }
  if (!isMobile) {
    options.position = 'top-left'
    options.className = `toast-scan-screen-${classSize}`
  }
  toast.error(message, options)
}

const QRPay = ({
  className,
  onGoBack,
  needCreateOrder,
  order,
  addPaymentToOrderInProgress,
  balanceDueInCents,
  placeFreeOrder,
  push,
}) => {
  const classNames = cn(styles.scanScreen, className)
  const isMobile = window.matchMedia('(max-width: 700px)')?.matches
  const [selectedSubmissionType, setSelectedSubmissionType] = useState(isMobile ? 'entry' : 'scan')
  const [isProcessing, setIsProcessing] = useState(false)
  const [qrCode, setQRCode] = useState('')
  const [createOrder, orderCreated] = useCreateOrderInProgress()
  const [getQRBalance, { qrBalance, vaultedCard, dataSucceeded, dataFailed, isExpired, affiliationUuids }] = useGetQRBalance()
  const [keyboardIsActive, setKeyboardIsActive] = useState(false)
  const [retotalingOrder, setRetotalingOrder] = useState(false)
  const [cfdQrPaySuccess, setCfdQrPaySuccess] = useState(false)
  const hasNoBalance = +qrBalance === 0 && !vaultedCard
  const [loadRemoteOrderTotal, {}, resetOrderTotals] = useRemoteOrderTotal()


  const qrCodeRef = useRef()
  const dispatch = useDispatch()
  const paymentProgress = useSelector(state => getPaymentProgress(state))
  const cfdMode = useSelector(state => getDeviceMode(state))
  const qrPaySuccess = useSelector(state => getQrPaySuccess(state)) // returns if the qr pay was successfully processed from the saas
  let enterKeyPressed = false // because POS is sometimes slow, pressing enter twice will apply discounts twice and mess totaling up.

  const closeKeyBoard = () => {
    if (keyboardIsActive) {
      qrCodeRef?.current?.blur()
    }
  }

  const handleFocusChange = () => {
    setTimeout(() => {
      setKeyboardIsActive(document.activeElement === qrCodeRef?.current)
    }, 150)
  }

  const qrCodeAlreadyUsed = (qrCode) => some(order?.payments, (payment) => payment?.qrCode == qrCode)

  useEffect(() => {
    if (needCreateOrder) {
      createOrder()
    }
    enterKeyPressed = false
    document.addEventListener('focusin', handleFocusChange)
    document.addEventListener('focusout', handleFocusChange)

    return () => {
      document.removeEventListener('focusin', handleFocusChange)
      document.removeEventListener('focusout', handleFocusChange)
      const bridgeInstance = PeripheralBridge.getBridge()
      if (!bridgeInstance) return
      bridgeInstance.call('stopScanner')
    }
  }, [])

  useEffect(() => {
    if (qrPaySuccess) {
      setCfdQrPaySuccess(true)
      sendCFDScreenNav(CFD_SCREEN.RECEIPT, {cartTotals: { uuid: order?.uuid}, transaction: {amount: centsToFloat(order?.amountInCents)}})
      dispatch(setQrPaySuccess(false))
    }
  }, [qrPaySuccess])

  useEffect(() => {
    if (dataFailed) {
      setIsProcessing(false)
      if (isExpired) {
        toastError('QR code has expired. Please refresh your code and try again', 'lg', isMobile)
      } else {
        toastError('QR code is not Valid. Please try again.', 'sm', isMobile)
      }
    }
  }, [dataFailed])

  useEffect(() => {
    if (orderCreated && order?.amountInCents === 0 && !retotalingOrder) {
      placeFreeOrder()
      return
    }
    if (retotalingOrder && orderCreated) {
      setRetotalingOrder(false)
      handleSuccess()
    }
  }, [orderCreated])

  useEffect(() => {
    if (dataSucceeded) {
      if (!isEmpty(affiliationUuids)) {
        createOrder(true, affiliationUuids, order?.tipAmountInCents)
        setRetotalingOrder(true)
        return
      }
      handleSuccess()
    }
  }, [dataSucceeded])

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (selectedSubmissionType === 'entry' && qrCodeRef.current) {
      qrCodeRef.current?.focus()
    }

    if (selectedSubmissionType === 'scan') {
      if (!bridgeInstance) return
      bridgeInstance.registerHandler('scanPayload', function (data, callback) {
        const scannedCode = data?.valueOf()
        if (qrCodeAlreadyUsed(scannedCode)) {
          toastError('QR code already scanned and applied to order', 'md', isMobile)
          return
        }

        setQRCode(scannedCode)
        getQRBalance(scannedCode)
        setIsProcessing(true)
      })

      bridgeInstance.registerHandler('scanError', function (err, callback) {
        if (err?.toLowerCase()?.includes('no scanner detected')) {
          setSelectedSubmissionType('entry')
        }
        toastError(`QR code scan error, ERROR: ${JSON.stringify(err)}`, 'lg', isMobile)
      })
    }
  }, [selectedSubmissionType])

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    if (isProcessing || selectedSubmissionType === 'entry') {
      bridgeInstance.call('stopScanner')
    } else if (selectedSubmissionType === 'scan') {
      bridgeInstance.call('startScanner')
    }
  }, [isProcessing, selectedSubmissionType])

  const primaryText = centsToDollars(balanceDueInCents !== 0 ? balanceDueInCents : order?.amountInCents)
  const secondaryText =
    selectedSubmissionType === 'scan'
      ? 'Please scan QR code'
      : 'Please type QR Pay code'

  const handleSubmissionTypeChange = value => {
    if (selectedSubmissionType !== value) {
      setSelectedSubmissionType(value)
    }
  }

  const redirect = (tenderAmountInCents) => {

    if (cfdMode === MODES.POS) {
      sendCFDScreenNav(CFD_SCREEN.BALANCE_DUE, { balanceToBePaid: { amountPaid: (order?.amountInCents - tenderAmountInCents), balanceDue: tenderAmountInCents}})
    }

    push({
      pathname: '/tender-selection',
      state: {
        tenderAmountInCents,
        scanScreenType: 'qr code',
      }
    })
  }

  const handleSuccess = () => {

    if (hasNoBalance && order?.amountInCents !== 0) {
      closeKeyBoard()
      toast.success('Scan QR code successful')
      redirect(balanceDueInCents)
      return
    }

    let tenderAmountInCents
    let redirectToSplit = false
    if (vaultedCard || qrBalance >= balanceDueInCents) {
      tenderAmountInCents = balanceDueInCents
    } else {
      tenderAmountInCents = qrBalance
      redirectToSplit = true;
    }

    addPaymentToOrderInProgress({
      tenderAmountInCents,
      paymentType: 'wallet_nonce',
      tenderDisplayName: 'QR Pay',
      token: qrCode,
    })
    closeKeyBoard()

    if (cfdMode === MODES.POS && !redirectToSplit) return
    if (redirectToSplit) {
      toast.success('Scan QR code successful')
      redirect(balanceDueInCents - qrBalance)
    }
  }

  const handleKeyPress = e => {
    if ((e.keyCode === KEY_CODES.ENTER || e.keyCode === '9' || e.key === 'ENTER' || e.key === 'Tab') && !enterKeyPressed) {
      enterKeyPressed = true
      const isValid = /^[a-z|0-9\-]*?$/gi.test(qrCode);
      if (!isValid) {
        toastError('The code entered contains invalid characters', 'md', isMobile)
        return
      }
      if (qrCodeAlreadyUsed(qrCode)) {
        toastError('QR Code already entered and applied to order', 'md', isMobile)
        return
      }
      getQRBalance(qrCode)
      setIsProcessing(true)
    }
  }

  const handleTicketCodeChange = e => {
    setQRCode(e?.target?.value)
  }

  if (!orderCreated && needCreateOrder) {
    return <ConnectingIndicator />
  }

  const chooseTenderOptionButton = dataFailed &&
    !isProcessing &&
    !keyboardIsActive && (
      <div
        className={styles.chooseTenderOption}
        onClick={() => {
          redirect(balanceDueInCents)
        }
        }
      >
        Pay With Other Method
      </div>
    )

  return (
    <div className={classNames} >
      {
        cfdMode === MODES.POS && 
        <VNDialog
          open={cfdQrPaySuccess}
          subTitle={parseInt(paymentProgress.index) === 4 ? 'Transaction Complete' : 'Customer Viewing Receipt'}
          showTextfield={false}
          onSave={() => {
            dispatch(clearCart())
            dispatch(clearPromotionsOnOrder())
            dispatch(clearOrderInProgress())
            dispatch(resetCFD(true))
            setCfdQrPaySuccess(false)
            sendCFDScreenNav(CFD_SCREEN.IDLE)
            resetOrderTotals()
            push('/concession-order')
          }}
          saveCountDownTime={parseInt(paymentProgress.index) === 4 ? 10 : undefined}
          closeOnSave={false}
          save={"NEW ORDER"}
          fullWidth
        />
      }
      <div className={styles.goBack}>
        <BackButtonChevron
          onClick={() => {
            closeKeyBoard()
            setTimeout(() => {
              onGoBack()
            }, 200)
            }
          }
          backButtonText={isMobile ? '' : 'Go Back'}
        />
        <span className={styles.mobileTitle}>{primaryText}</span>
        <div className={styles.contentRight}>
          <NetworkIndicator />
        </div>
      </div>
      <div className={styles.scanScreen__header}>
        <GratuityHeader
          className={styles.gratuityHeader}
          primaryText={isMobile ? '' : primaryText}
          secondaryText={secondaryText}
        />
      </div>
      {!isMobile && <div className={styles.scanScreen__submissionChoices}>
        <SegmentedControlV2
          className={styles.segmentedControl}
          items={QRChoices}
          selectedValue={selectedSubmissionType}
          onSelect={handleSubmissionTypeChange}
        />
      </div>}
      <div className={styles.scanScreen__body}>
        <div className={styles.choicesContent}>
          <div
            className={cn(styles.option, {
              [styles.isHidden]:
                selectedSubmissionType === 'entry' || isProcessing
            })}
          >
            <img src={ScanQRGif} alt={"Scan QR"} className={styles.qrImage}/>
          </div>
          <div
            className={cn(styles.option, {
              [styles.isHidden]:
                selectedSubmissionType === 'scan' || isProcessing
            })}
          >
            <input
              className={styles.manualEntry}
              ref={qrCodeRef}
              value={qrCode}
              onChange={handleTicketCodeChange}
              onKeyDown={handleKeyPress}
              type='tel'
            />
          </div>
          <div
            className={cn(styles.processing, {
              [styles.isHidden]: !isProcessing
            })}
          >
            <div className={styles.loadingSpinner}>
              <ConnectingIndicator position={'initial'}/>
            </div>
            <span className={styles.topText}>Processing...</span>
            <span className={styles.bottomText}>please wait.</span>
          </div>
        </div>
        {chooseTenderOptionButton}
      </div>
    </div>
  )
}

const mapStateToProps = (state, props) => {
  const order = getOrderInProgress(state)
  const balanceDueInCents = order?.balanceDueInCents
  const push = props?.history?.push ?? (() => {})

  return {
    order,
    needCreateOrder: isEmpty(order),
    balanceDueInCents,
    push,
  }
}

const mapDispatchToProps = (dispatch, props) => {
  const goBack = props?.history?.goBack ?? (() => {})

  return {
    onGoBack: () => {
      dispatch(clearOrderInProgress())
      goBack()
    },
    addPaymentToOrderInProgress: payment =>
      dispatch(addPaymentToOrderInProgress(payment)),
    placeFreeOrder: () => {
      dispatch(closeNoOpTender())
    },
  }
}
export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(QRPay)
)

